import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/header';
import Footer from '../components/footer';
import TestimonialVideoCarousel from '../components/TestimonialVideoCarousel';
import SpecialtyDoctorsCarousel from '../components/SpecialtyDoctorsCarousel';
import CallbackPopup from '../components/CallbackPopup';
import FloatingChatButton from '../components/FloatingChatButton';
import ReCAPTCHA from "react-google-recaptcha";
import { countries } from '../constants/countries';

const TreatmentsItemPage = () => {
  const { slug } = useParams();
  const [treatment, setTreatment] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('India');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form logic for the right column
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '',
    mobile: '',
    email: '',
    preferredLanguage: '',
    country: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [isCallbackPopupOpen, setIsCallbackPopupOpen] = useState(false);

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcyZ50rAAAAALJS9q7T5m_U115V1UYbM6b20PjA';

  const countryOptions = ['India', 'Turkey', 'Thailand', 'South Korea'];

  useEffect(() => {
    // If slug is provided, fetch dynamic data, otherwise use fallback
    if (slug) {
      fetchTreatment();
    } else {
      // Use fallback data for backward compatibility
      setTreatment({
        title: "Bariatric Combination Agents",
        description: "Bariatric Combination Agents (BCA) include personalized medical weight-loss regimens that use a mix of appetite suppressants, insulin sensitizers, and hormonal modifiers. These drugs are prescribed to support weight loss in patients with obesity or metabolic diseases where surgery isn't the first line of treatment.",
        headerImage: "/images/Treatment Item Page - Header Image 839Wx482H PX.png",
        costInfo: {
          India: { minCost: '$3500', maxCost: '$5500', totalStay: '11 Days', hospitalStay: '03 Days' },
          Turkey: { minCost: '$4000', maxCost: '$6000', totalStay: '12 Days', hospitalStay: '04 Days' },
          Thailand: { minCost: '$3800', maxCost: '$5800', totalStay: '10 Days', hospitalStay: '03 Days' },
          'South Korea': { minCost: '$5000', maxCost: '$7500', totalStay: '14 Days', hospitalStay: '05 Days' }
        },
        typesOfTreatment: "Combination Protocols – Tailored regimens combining agents like Metformin, Naltrexone–Bupropion, Topiramate, and GLP-1 analogues. Therapy may also include low-dose diuretics, thyroid modulators, or SGLT2 inhibitors.",
        diagnosis: "Evaluation includes complete metabolic workup, BMI measurement, dietary history, hormone testing, and insulin resistance assessment. Cardiac risk, liver and kidney function, and psychological readiness are also assessed before starting therapy.",
        diagnosticCosts: [
          { procedure: "Blood Tests", costRange: "$70 - $120" },
          { procedure: "Thyroid, Cortisol, and Other Hormones", costRange: "$70 - $120" },
          { procedure: "Imaging (Ultrasound Abdomen, ECG)", costRange: "$70 - $120" }
        ],
        treatmentCostsByCountry: [
          { country: "India", costRange: "$3500 - $5500" },
          { country: "Turkey", costRange: "$4000 - $6000" },
          { country: "Thailand", costRange: "$3800 - $5800" },
          { country: "South Korea", costRange: "$5000 - $7500" },
          { country: "USA", costRange: "$8000 - $12000" }
        ],
        treatmentCostsInIndia: [
          { procedure: "Routine patient investigations before surgery", costRange: "$70 - $120" },
          { procedure: "Nursing fee", costRange: "$70 - $120" },
          { procedure: "ICU stay if needed", costRange: "$70 - $120" },
          { procedure: "Surgeon fee, OT charges, anesthesia", costRange: "$70 - $120" }
        ],
        treatmentCostInIndianCities: [
          { city: "Mumbai", cost: "$4500 - $5500" },
          { city: "Delhi", cost: "$4200 - $5200" },
          { city: "Bangalore", cost: "$4000 - $5000" },
          { city: "Chennai", cost: "$3800 - $4800" }
        ],
        associatedSpecialities: ["Bariatric Surgery", "Endocrinology", "Internal Medicine"],
        factorsAffectingCost: "Please note that the costs mentioned above are only indicative and not conclusive, as multiple factors influence the final cost of your treatment journey. These factors include: The choice of location, doctor and hospital; pre-existing medical history; specific diagnostic combinations; duration of therapy; doctor monitoring frequency; lifestyle interventions; post-treatment counselling; and any special lab or pharmacy requirements."
      });
      setLoading(false);
    }
    fetchHospitals();
  }, [slug]);

  const fetchTreatment = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://medical-linkers.onrender.com/api/treatments/${slug}`);
      const data = await response.json();
      
      if (response.ok) {
        setTreatment(data);
      } else {
        setError('Treatment not found');
      }
    } catch (error) {
      console.error('Error fetching treatment:', error);
      setError('Failed to fetch treatment data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHospitals = async () => {
    try {
      const response = await fetch('https://medical-linkers.onrender.com/api/hospitals');
      const data = await response.json();
      
      if (response.ok) {
        setHospitals(data.hospitals || data.data || []);
      }
    } catch (error) {
      console.error('Error fetching hospitals:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validate reCAPTCHA
    if (!recaptchaValue) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the reCAPTCHA verification'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://medical-linkers.onrender.com/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          formType: 'contact',
          recaptchaToken: recaptchaValue
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ type: 'success', message: 'Thank you! Your message has been sent successfully.' });
        setFormData({
          name: '',
          countryCode: '',
          mobile: '',
          email: '',
          preferredLanguage: '',
          country: '',
          city: '',
          message: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: data.message || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChatNow = (hospital) => {
    // Create WhatsApp message
    const message = `Hello! I'm interested in ${treatment?.title || 'medical treatment'} at ${hospital.name}. Could you please provide more information about the treatment options and costs?`;
    const encodedMessage = encodeURIComponent(message);
    
    // WhatsApp business number (you may want to use hospital-specific numbers if available)
    // Using a generic number for now - replace with actual WhatsApp business number
    const phoneNumber = hospital.whatsapp || '9599222655'; // Default number, should be replaced
    
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleBookAppointment = (hospital) => {
    setIsCallbackPopupOpen(true);
  };

  // Social media sharing handlers
  const handleSocialShare = (platform) => {
    const currentUrl = window.location.href;
    const title = `${treatment?.title} - Medical Treatment Information`;
    const description = treatment?.description || 'Get detailed information about this medical treatment including costs, procedures, and top hospitals.';
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}&quote=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${title}\n\n${description}\n\nLearn more: ${currentUrl}`)}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(title)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const getCountryCostInfo = () => {
    if (!treatment?.costInfo) return null;
    
    return treatment.costInfo[selectedCountry] || treatment.costInfo['India'];
  };

  const filterHospitalsBySpecialities = () => {
    if (!treatment?.associatedSpecialities?.length) return hospitals.filter(h => h.country === 'India');
    
    return hospitals.filter(hospital => 
      treatment.associatedSpecialities.some(treatmentSpec => 
        hospital.specialities?.some(hospitalSpec => 
          hospitalSpec.toLowerCase().includes(treatmentSpec.toLowerCase()) ||
          treatmentSpec.toLowerCase().includes(hospitalSpec.toLowerCase())
        )
      )
    ).filter(hospital => hospital.country === 'India');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col font-sans">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="text-xl text-blue-600">Loading treatment details...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !treatment) {
    return (
      <div className="min-h-screen bg-white flex flex-col font-sans">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="text-xl text-red-600">{error || 'Treatment not found'}</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const countryInfo = getCountryCostInfo();
  const filteredHospitals = filterHospitalsBySpecialities();

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      <Header />
      <main className="flex-grow container mx-auto px-4">
        {/* Treatment Details Section */}
        <div className="py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#1976f2] mb-4 font-montserrat">{treatment.title}</h1>
              <p className="text-black leading-relaxed text-left font-open-sans">
                {treatment.description}
              </p>
            </div>

            {/* Image Content */}
            <div className="flex justify-center">
              <img
                src={treatment.headerImage || "/images/Treatment Item Page - Header Image 839Wx482H PX.png"}
                alt={treatment.title}
                className="w-full max-w-2xl rounded-xl mb-8 object-cover"
                style={{ height: 320 }}
                onError={(e) => { 
                  e.target.onerror = null; 
                  e.target.src = 'https://placehold.co/600x400/cccccc/ffffff?text=Image+Not+Found'; 
                }}
              />
            </div>
          </div>
        </div>

        {/* Cost & Stay Information Section */}
        <div className="py-8">
          <div className="flex items-center space-x-4 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 font-montserrat">Cost in</h2>
            <div className="relative">
              <select 
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="appearance-none bg-white border-2 border-[#1976f2] rounded-lg pl-4 pr-10 py-2 text-[#1976f2] font-semibold focus:outline-none focus:border-[#1976f2] cursor-pointer"
              >
                {countryOptions.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-blue-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
          </div>

          {countryInfo && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-black text-lg font-bold">Min Cost</p>
                <p className="text-[#1976f2] text-3xl font-bold">${countryInfo.minCost}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-black text-lg font-bold">Max Cost</p>
                <p className="text-[#1976f2] text-3xl font-bold">${countryInfo.maxCost}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-black text-lg font-bold">Total Stay</p>
                <p className="text-[#1976f2] text-3xl font-bold">{countryInfo.totalStay}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-black text-lg font-bold">Hospital Stay</p>
                <p className="text-[#1976f2] text-3xl font-bold">{countryInfo.hospitalStay}</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* New Sections Start */}
      <section className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-4">Types of Treatments</h2>
            <p className="mb-8 text-black">{treatment.typesOfTreatment}</p>

            <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-4">Diagnosis</h2>
            <p className="mb-8 text-black">{treatment.diagnosis}</p>

            {/* Diagnostic Costs Table */}
            {treatment.diagnosticCosts && treatment.diagnosticCosts.length > 0 && (
              <>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-4">Diagnostic Costs</h2>
                <table className="w-full mb-8 border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left font-bold">Diagnostic Procedure</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-bold">Cost in USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {treatment.diagnosticCosts.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 px-4 py-2">{item.procedure}</td>
                        <td className="border border-gray-300 px-4 py-2">{item.costRange}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Treatment Cost in Countries Table */}
            {treatment.treatmentCostsByCountry && treatment.treatmentCostsByCountry.length > 0 && (
              <>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-4">Treatment Cost in Countries</h2>
                <table className="w-full mb-8 border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left font-bold">Country</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-bold">Cost in USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {treatment.treatmentCostsByCountry.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 px-4 py-2">{item.country}</td>
                        <td className="border border-gray-300 px-4 py-2">{item.costRange}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Treatment Cost Inclusions Table */}
            {treatment.treatmentCostsInIndia && treatment.treatmentCostsInIndia.length > 0 && (
              <>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-4">Treatment Cost Inclusions</h2>
                <table className="w-full mb-8 border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left font-bold">Service</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-bold">Cost in USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {treatment.treatmentCostsInIndia.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 px-4 py-2">{item.procedure}</td>
                        <td className="border border-gray-300 px-4 py-2">{item.costRange}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Treatment Cost in Indian Cities Table */}
            {treatment.treatmentCostInIndianCities && treatment.treatmentCostInIndianCities.length > 0 && (
              <>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-4">Treatment Cost in Indian Cities</h2>
                <table className="w-full mb-8 border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left font-bold">City</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-bold">Cost in USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {treatment.treatmentCostInIndianCities.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 px-4 py-2">{item.city}</td>
                        <td className="border border-gray-300 px-4 py-2">{item.cost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* Right Form */}
          <div className="sticky top-24">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-center">Get treated at your preferred hospital</h2>
              <p className="text-center text-gray-500 mb-6">Start your treatment journey now</p>
              {submitStatus && (
                <div className={`p-4 rounded mb-4 text-center ${submitStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {submitStatus.message}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  className="w-full border border-gray-300 p-3 rounded font-open-sans"
                />
                <div className="flex gap-2">
                  <select 
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="w-1/3 border border-gray-300 p-3 rounded font-open-sans"
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.code + country.name} value={country.code}>
                        {country.flag} {country.name} ({country.code})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Mobile Number"
                    required
                    className="w-2/3 border border-gray-300 p-3 rounded font-open-sans"
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  required
                  className="w-full border border-gray-300 p-3 rounded font-open-sans"
                />
                <input
                  type="text"
                  name="preferredLanguage"
                  value={formData.preferredLanguage}
                  onChange={handleChange}
                  placeholder="Preferred Language"
                  className="w-full border border-gray-300 p-3 rounded font-open-sans"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                    className="w-1/2 border border-gray-300 p-3 rounded font-open-sans"
                  />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    className="w-1/2 border border-gray-300 p-3 rounded font-open-sans"
                  />
                </div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe Your Questions"
                  required
                  className="w-full border border-gray-300 p-3 rounded h-24 font-open-sans"
                ></textarea>
                
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-center">
                    <ReCAPTCHA
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={() => setRecaptchaValue(null)}
                      theme="light"
                    />
                  </div>
                  {!recaptchaValue && submitStatus?.type === 'error' && submitStatus.message.includes('reCAPTCHA') && (
                    <p className="text-red-500 text-sm text-center font-open-sans">Please complete the reCAPTCHA verification</p>
                  )}
                </div>
                
                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#1976f2] text-white px-6 py-2 rounded hover:bg-blue-600 transition disabled:opacity-50 font-montserrat"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>

                {submitStatus && (
                  <div className={`text-center p-3 rounded-md font-open-sans ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Top Hospitals for this Treatment */}
      <section className="container mx-auto px-4 py-12">
        <div className="w-full">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-6">
            Top Hospitals in India for this Treatment
          </h2>
          <div className="space-y-6">
            {filteredHospitals.length > 0 ? (
              filteredHospitals.slice(0, 5).map((hospital, idx) => (
                <div key={hospital._id || idx} className="border-2 border-[#1976f2] rounded-lg p-4 flex flex-col md:flex-row items-center gap-4 bg-white">
                  <img 
                    src={hospital.mainImage || 'https://placehold.co/150x100/cccccc/ffffff?text=Hospital'} 
                    alt={hospital.name} 
                    className="w-32 h-24 object-cover rounded-lg" 
                    onError={(e) => { 
                      e.target.onerror = null; 
                      e.target.src = 'https://placehold.co/150x100/cccccc/ffffff?text=Hospital'; 
                    }}
                  />
                  <div className="flex-1 text-left">
                    <h3 className="text-lg font-bold text-[#1976f2] mb-1">
                      <a 
                        href={`/hospital/${hospital.slug || (hospital.name ? hospital.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : '')}`}
                        className="hover:underline hover:text-blue-700 transition-colors"
                      >
                        {hospital.name}
                      </a>
                    </h3>
                    <p className="text-gray-700 text-sm mb-2 line-clamp-2">{hospital.description}</p>
                    <div className="flex items-center gap-4">
                      <span className="text-yellow-500 font-bold">BEDS: {hospital.totalBeds || 'N/A'}</span>
                      <button 
                        onClick={() => handleChatNow(hospital)}
                        className="bg-[#1976f2] text-white px-3 py-1 rounded text-xs font-semibold hover:bg-blue-600 transition-colors flex items-center gap-1"
                      >
                        <i className="ri-whatsapp-line"></i> Chat Now
                      </button>
                      <button 
                        onClick={() => handleBookAppointment(hospital)}
                        className="border border-[#1976f2] text-[#1976f2] px-3 py-1 rounded text-xs font-semibold hover:bg-[#1976f2] hover:text-white transition-colors"
                      >
                        Book Appointment
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No hospitals found for this treatment in India</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Factors Affecting This Particular Treatment Cost */}
      <section className="container mx-auto px-4 py-12">
        <div className="w-full">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-4">
            Factors Affecting This Particular Treatment Cost in India
          </h2>
          <p className="text-black mb-8">
            {treatment.factorsAffectingCost || 
              `Please note that the costs mentioned above are only indicative and not conclusive, as multiple factors influence the final cost of your treatment journey. These factors include:
              The choice of location, doctor and hospital; pre-existing medical history; specific diagnostic combinations; duration of therapy; doctor monitoring frequency; lifestyle interventions; post-treatment counselling; and any special lab or pharmacy requirements.`
            }
          </p>
        </div>
      </section>

      {/* Testimonials Section - Now using TestimonialVideoCarousel */}
      <section className="container mx-auto px-4 py-12">
        <div>
          
          <TestimonialVideoCarousel />
        </div>
      </section>

      {/* Top Doctors - Now using SpecialtyDoctorsCarousel */}
      <section className="container mx-auto px-4 py-12">
        <div className="w-full">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-center">
            <span className="text-[#1976f2]">Top Doctors for</span> <span className="text-black">{treatment?.associatedSpecialities && treatment.associatedSpecialities.length > 0 ? treatment.associatedSpecialities[0] : 'this Treatment'}</span>
          </h2>
          <SpecialtyDoctorsCarousel 
            specialties={treatment?.associatedSpecialities || []} 
            treatmentTitle={treatment?.title || ''}
          />
          
          {/* Share This Treatment Plan */}
          <div className="mb-2 w-full flex flex-col items-center mt-8">
            <h3 className="text-3xl md:text-4xl font-extrabold mb-6 text-center">
              <span className="text-[#1976f2]">Share This</span> <span className="text-black">Treatment Plan</span>
            </h3>
            <div className="flex flex-row gap-10 text-5xl justify-center items-center">
              <button 
                onClick={() => handleSocialShare('facebook')}
                className="text-black hover:text-[#1976f2] transition-colors cursor-pointer"
                aria-label="Share on Facebook"
              >
                <i className="fab fa-facebook"></i>
              </button>
              <button 
                onClick={() => handleSocialShare('linkedin')}
                className="text-black hover:text-[#1976f2] transition-colors cursor-pointer"
                aria-label="Share on LinkedIn"
              >
                <i className="fab fa-linkedin"></i>
              </button>
              <button 
                onClick={() => handleSocialShare('whatsapp')}
                className="text-black hover:text-[#1976f2] transition-colors cursor-pointer"
                aria-label="Share on WhatsApp"
              >
                <i className="fab fa-whatsapp"></i>
              </button>
              <button 
                onClick={() => handleSocialShare('telegram')}
                className="text-black hover:text-[#1976f2] transition-colors cursor-pointer"
                aria-label="Share on Telegram"
              >
                <i className="fab fa-telegram"></i>
              </button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />

      {/* Floating Chat Button */}
      <FloatingChatButton />

      {/* Callback Popup */}
      <CallbackPopup 
        isOpen={isCallbackPopupOpen} 
        onClose={() => setIsCallbackPopupOpen(false)} 
      />
    </div>
  );
};

export default TreatmentsItemPage;